import { useModel as _useModel, defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

import { EnetAPI } from '@/api';
import BaseGridCol from '@/components/BaseGridCol.vue';
import BaseGridRow from '@/components/BaseGridRow.vue';
import InputField from '@/components/InputField.vue';
import SelectField from '@/components/SelectField.vue';
import CityInterface from '@/interfaces/CityInterface';
import StreetInterface from '@/interfaces/StreetInterface';
import { computed, watch, ref } from 'vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'AppAddressFields',
  props: {
    "firstName": {},
    "firstNameModifiers": {},
    "errorFirstName": {},
    "errorFirstNameModifiers": {},
    "lastName": {},
    "lastNameModifiers": {},
    "errorLastName": {},
    "errorLastNameModifiers": {},
    "postcode": {},
    "postcodeModifiers": {},
    "errorPostcode": {},
    "errorPostcodeModifiers": {},
    "city": {},
    "cityModifiers": {},
    "errorCity": {},
    "errorCityModifiers": {},
    "street": {},
    "streetModifiers": {},
    "errorStreet": {},
    "errorStreetModifiers": {},
    "houseNumber": {},
    "houseNumberModifiers": {},
    "errorHouseNumber": {},
    "errorHouseNumberModifiers": {},
    "houseNumberAddon": {},
    "houseNumberAddonModifiers": {},
    "errorHouseNumberAddon": {},
    "errorHouseNumberAddonModifiers": {},
  },
  emits: ["update:firstName", "update:errorFirstName", "update:lastName", "update:errorLastName", "update:postcode", "update:errorPostcode", "update:city", "update:errorCity", "update:street", "update:errorStreet", "update:houseNumber", "update:errorHouseNumber", "update:houseNumberAddon", "update:errorHouseNumberAddon"],
  setup(__props) {

const firstName = _useModel(__props, 'firstName');
const errorFirstName = _useModel(__props, 'errorFirstName');
const lastName = _useModel(__props, 'lastName');
const errorLastName = _useModel(__props, 'errorLastName');
const postcode = _useModel(__props, 'postcode');
const errorPostcode = _useModel(__props, 'errorPostcode');
const city = _useModel(__props, 'city');
const errorCity = _useModel(__props, 'errorCity');
const street = _useModel(__props, 'street');
const errorStreet = _useModel(__props, 'errorStreet');
const houseNumber = _useModel(__props, 'houseNumber');
const errorHouseNumber = _useModel(__props, 'errorHouseNumber');
const houseNumberAddon = _useModel(__props, 'houseNumberAddon');
const errorHouseNumberAddon = _useModel(__props, 'errorHouseNumberAddon');

const selectableCities = ref<CityInterface[]>([]);
const selectableStreets = ref<StreetInterface[]>([]);
const postcodeLoaded = ref<string>('');
const isFetching = ref<boolean>(false);

const fetchCitiesAndStreets = ($postcode: string | number) => {
  if (`${$postcode}`.length === 5 && $postcode !== postcodeLoaded.value) {
    postcodeLoaded.value = $postcode as string;
    isFetching.value = true;

    EnetAPI.getCitiesAndStreets($postcode).then((data) => {
      selectableCities.value = data.cities;
      if (data.cities.length === 1) {
        city.value = data.cities[0].city;
      } else if (city.value) {
        const responseCity = (data.cities || []).find((resCity) => resCity.city === city.value);

        if (!responseCity) {
          city.value = undefined;
        }
      }
      selectableStreets.value = data.streets;
      if (street.value !== undefined) {
        const responseStreet = (data.streets || []).find((resStreet) => resStreet.street === street.value);

        if (!responseStreet) {
          street.value = undefined;
        }
      }
    }).catch(() => {
      selectableCities.value = [];
      selectableStreets.value = [];
      city.value = undefined;
      street.value = undefined;
    }).finally(() => {
      isFetching.value = false;
    });
  }
};

watch(() => postcode.value, (newPostcode: any) => {
  fetchCitiesAndStreets(newPostcode as string);
});

if (postcode.value) {
  fetchCitiesAndStreets(postcode.value as string);
}

const citiesOptions = computed(() => (
  [...selectableCities.value].map(($c) => ({
    label: $c.city,
    value: $c.city,
  }))));

const streetsOptions = computed(() => (
  [...selectableStreets.value].map(($s) => ({
    label: $s.street,
    value: $s.street,
  }))));

const showStreets = computed(() => (
  postcode.value
    && `${postcode.value}`.length === 5
    && streetsOptions.value.length
));

const showEmptyCity = computed(() => (
  `${postcode.value}`.length !== 5
    || citiesOptions.value.length === 0
));

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(BaseGridRow, {
    small: "",
    "default-col-size": 6
  }, {
    default: _withCtx(() => [
      _createVNode(BaseGridCol, { s: { xs: 12, lg: 6 } }, {
        default: _withCtx(() => [
          _createVNode(InputField, {
            label: "Vorname",
            type: "text",
            modelValue: firstName.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((firstName).value = $event)),
            error: errorFirstName.value,
            "onUpdate:error": _cache[1] || (_cache[1] = ($event: any) => ((errorFirstName).value = $event))
          }, null, 8, ["modelValue", "error"])
        ]),
        _: 1
      }),
      _createVNode(BaseGridCol, { s: { xs: 12, lg: 6 } }, {
        default: _withCtx(() => [
          _createVNode(InputField, {
            label: "Nachname",
            type: "text",
            modelValue: lastName.value,
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((lastName).value = $event)),
            error: errorLastName.value,
            "onUpdate:error": _cache[3] || (_cache[3] = ($event: any) => ((errorLastName).value = $event))
          }, null, 8, ["modelValue", "error"])
        ]),
        _: 1
      }),
      _createVNode(BaseGridCol, { s: { xs: 12, lg: 6 } }, {
        default: _withCtx(() => [
          _createVNode(InputField, {
            label: "Postleitzahl*",
            "only-numeric": true,
            "max-length": 5,
            "min-length": 5,
            "show-loader": isFetching.value,
            modelValue: postcode.value,
            "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((postcode).value = $event)),
            error: errorPostcode.value,
            "onUpdate:error": _cache[5] || (_cache[5] = ($event: any) => ((errorPostcode).value = $event))
          }, null, 8, ["show-loader", "modelValue", "error"])
        ]),
        _: 1
      }),
      _createVNode(BaseGridCol, { s: { xs: 12, lg: 6 } }, {
        default: _withCtx(() => [
          (showEmptyCity.value)
            ? (_openBlock(), _createBlock(InputField, {
                key: 0,
                label: "Stadt*",
                readonly: "",
                disabled: ""
              }))
            : (citiesOptions.value.length > 1)
              ? (_openBlock(), _createBlock(SelectField, {
                  key: 1,
                  label: "Stadt*",
                  modelValue: city.value,
                  "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((city).value = $event)),
                  options: citiesOptions.value,
                  error: errorCity.value,
                  "onUpdate:error": _cache[7] || (_cache[7] = ($event: any) => ((errorCity).value = $event))
                }, null, 8, ["modelValue", "options", "error"]))
              : (_openBlock(), _createBlock(InputField, {
                  key: 2,
                  label: "Stadt*",
                  readonly: "",
                  disabled: "",
                  "model-value": city.value,
                  error: errorCity.value,
                  "onUpdate:error": _cache[8] || (_cache[8] = ($event: any) => ((errorCity).value = $event))
                }, null, 8, ["model-value", "error"]))
        ]),
        _: 1
      }),
      (showStreets.value)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
            _createVNode(BaseGridCol, {
              s: 12,
              order: 3
            }, {
              default: _withCtx(() => [
                _createVNode(SelectField, {
                  label: "Straße*",
                  modelValue: street.value,
                  "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((street).value = $event)),
                  options: streetsOptions.value,
                  search: true,
                  error: errorStreet.value,
                  "onUpdate:error": _cache[10] || (_cache[10] = ($event: any) => ((errorStreet).value = $event))
                }, null, 8, ["modelValue", "options", "error"])
              ]),
              _: 1
            }),
            _createVNode(BaseGridCol, {
              s: { xs: 12, lg: 6 },
              order: 4
            }, {
              default: _withCtx(() => [
                _createVNode(InputField, {
                  label: "Haus-Nr.*",
                  type: "text",
                  modelValue: houseNumber.value,
                  "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((houseNumber).value = $event)),
                  error: errorHouseNumber.value,
                  "onUpdate:error": _cache[12] || (_cache[12] = ($event: any) => ((errorHouseNumber).value = $event))
                }, null, 8, ["modelValue", "error"])
              ]),
              _: 1
            }),
            _createVNode(BaseGridCol, {
              s: { xs: 12, lg: 6 },
              order: 5
            }, {
              default: _withCtx(() => [
                _createVNode(InputField, {
                  label: "Hausnummerzusatz",
                  type: "text",
                  modelValue: houseNumberAddon.value,
                  "onUpdate:modelValue": _cache[13] || (_cache[13] = ($event: any) => ((houseNumberAddon).value = $event)),
                  error: errorHouseNumberAddon.value,
                  "onUpdate:error": _cache[14] || (_cache[14] = ($event: any) => ((errorHouseNumberAddon).value = $event))
                }, null, 8, ["modelValue", "error"])
              ]),
              _: 1
            })
          ], 64))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }))
}
}

})